// 차트 영역 강조(chartjs-plugin-annotation) 공용 유틸
// 저장 옵션(JSON, 직렬화 가능) ↔ chart.js plugin 옵션 변환 책임을 담당한다.
// 저장 옵션은 chartData.options.plugins.annotations 배열에 보관한다.
//
// chart.js 4 + chartjs-plugin-annotation 3 조합에서 instance plugin (new Chart 의 plugins 배열)
// 만으로는 chart.options.plugins.annotation 옵션 namespace 가 활성화되지 않는 경우가 있어
// 글로벌 등록(Chart.register)을 함께 수행한다. import 시점에 자동 등록되도록 모듈 최상단에서 호출.
import { Chart } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
Chart.register(annotationPlugin);

export const annotation_type_options = [
  { value: 'box', text: '영역 박스(box)' },
  { value: 'line', text: '기준선(line)' },
  { value: 'point', text: '점(point)' }
];

export const annotation_axis_options = [
  { value: 'y', text: 'Y축(가로 띠/수평선)' },
  { value: 'x', text: 'X축(세로 띠/수직선)' }
];

export const annotation_label_position_options = [
  { value: 'start', text: '시작' },
  { value: 'center', text: '가운데' },
  { value: 'end', text: '끝' }
];

// 라벨 폰트 패밀리: 프로젝트 기본 폰트와 통일
const LABEL_FONT_FAMILY = 'Pretendard';

// 영역 강조가 의미 있는 차트 타입만 허용 (각도/원형 차트는 제외)
const SUPPORTED_CHART_TYPES = ['bar', 'line', 'mixed'];

export const DEFAULT_ANNOTATION = Object.freeze({
  type: 'box',
  label: '',
  axis: 'y',
  min: null,
  max: null,
  value: null,
  xValue: null,
  yValue: null,
  pointRadius: 6,
  backgroundColor: 'rgba(255, 193, 7, 0.15)',
  borderColor: 'rgba(255, 193, 7, 0.6)',
  borderWidth: 1,
  labelEnabled: true,
  labelPositionX: 'start',
  labelPositionY: 'start',
  labelColor: '#333333',
  labelFontSize: 11
});

// 빈 문자열/undefined는 null 로, 숫자 문자열은 Number 로 정규화
function _normalizeAxisValue(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

// vue-color picker는 변경 직후 객체 형태로 들어올 수 있어 한 번 더 sanitize
function _sanitizeColor(c, fallback) {
  if (c === null || c === undefined || c === '') return fallback;
  if (typeof c === 'string') return c;
  if (typeof c === 'object') {
    if ('hex8' in c && c.hex8) return c.hex8;
    if ('hex' in c && c.hex) return c.hex;
  }
  return fallback;
}

// box 는 label.position 이 { x, y } 객체를 지원하지만 line 은 단일 문자열 (선 방향 따라).
// line 의 경우 축에 따라 의미 있는 방향만 적용한다.
//  - axis='y' (수평선) → 선이 X축 방향으로 그려지므로 X 정렬을 사용
//  - axis='x' (수직선) → 선이 Y축 방향으로 그려지므로 Y 정렬을 사용
function _resolveLabelPosition(saved, annotationType) {
  const x = saved.labelPositionX || 'start';
  const y = saved.labelPositionY || 'start';
  if (annotationType === 'line') {
    return (saved.axis || 'y') === 'y' ? x : y;
  }
  return { x, y };
}

// chartjs-plugin-annotation 의 resolveObj 가 defaults.label 같은 nested 객체를 walk 할 때
// 우리가 label key를 omit 하면 nested fallback이 깨지면서 `Cannot read properties of undefined`
// 에러가 발생한다. 따라서 라벨 disabled 여도 label 객체를 항상 보내고, plugin defaults 의
// nested sub-object (callout, font) 도 빈/기본값으로 채워준다.
function _buildLabel(saved, annotationType) {
  const enabled = !!(saved.labelEnabled && saved.label);
  return {
    display: enabled,
    content: enabled ? String(saved.label) : '',
    position: _resolveLabelPosition(saved, annotationType),
    color: _sanitizeColor(saved.labelColor, DEFAULT_ANNOTATION.labelColor),
    backgroundColor: 'transparent',
    font: {
      family: LABEL_FONT_FAMILY,
      size: Number(saved.labelFontSize) || DEFAULT_ANNOTATION.labelFontSize
    },
    padding: 4,
    callout: { display: false }
  };
}

function _attachLabel(obj, saved, annotationType) {
  obj.label = _buildLabel(saved, annotationType);
  return obj;
}

function _toBoxAnnotation(saved) {
  const min = _normalizeAxisValue(saved.min);
  const max = _normalizeAxisValue(saved.max);
  if (min === null && max === null) return null;

  const isY = (saved.axis || 'y') === 'y';
  const obj = {
    type: 'box',
    ...(isY ? { yMin: min, yMax: max } : { xMin: min, xMax: max }),
    backgroundColor: _sanitizeColor(saved.backgroundColor, DEFAULT_ANNOTATION.backgroundColor),
    borderColor: _sanitizeColor(saved.borderColor, DEFAULT_ANNOTATION.borderColor),
    borderWidth: Number(saved.borderWidth) || 0
  };
  return _attachLabel(obj, saved, 'box');
}

function _toLineAnnotation(saved) {
  const value = _normalizeAxisValue(saved.value);
  if (value === null) return null;

  const obj = {
    type: 'line',
    scaleID: saved.axis || 'y',
    value,
    borderColor: _sanitizeColor(saved.borderColor, DEFAULT_ANNOTATION.borderColor),
    borderWidth: Number(saved.borderWidth) || 2,
    // LineAnnotation.defaults 의 nested 객체 (arrowHeads, controlPoint)를 항상 채움 — _buildLabel 주석 참고
    arrowHeads: { display: false, end: {}, start: {} },
    controlPoint: { y: '-50%' }
  };
  return _attachLabel(obj, saved, 'line');
}

function _toPointAnnotation(saved) {
  const x = _normalizeAxisValue(saved.xValue);
  const y = _normalizeAxisValue(saved.yValue);
  if (x === null && y === null) return null;

  const obj = {
    type: 'point',
    xValue: x,
    yValue: y,
    radius: Number(saved.pointRadius) || DEFAULT_ANNOTATION.pointRadius,
    backgroundColor: _sanitizeColor(saved.backgroundColor, DEFAULT_ANNOTATION.backgroundColor),
    borderColor: _sanitizeColor(saved.borderColor, DEFAULT_ANNOTATION.borderColor),
    borderWidth: Number(saved.borderWidth) || 1
  };
  // PointAnnotation.defaults 에는 label 이 없어 plugin 이 무시하지만,
  // 데이터 일관성을 위해 동일하게 attach 해둔다.
  return _attachLabel(obj, saved, 'point');
}

// 한 건의 저장 옵션을 chart.js annotation 객체로 변환
// 좌표값이 모두 비어있는 미완성 항목은 null 반환 → 렌더에서 skip
function _resolveOne(saved) {
  if (!saved) return null;
  const type = saved.type || 'box';
  if (type === 'line') return _toLineAnnotation(saved);
  if (type === 'point') return _toPointAnnotation(saved);
  return _toBoxAnnotation(saved);
}

// 저장된 배열 → chartjs-plugin-annotation 옵션 객체로 변환
// 지원되지 않는 차트 타입(pie/doughnut/polarArea/radar)에서는 빈 객체 반환
export function resolveAnnotationOptions(savedArray, chartType) {
  if (!SUPPORTED_CHART_TYPES.includes(chartType)) {
    return { annotations: {} };
  }
  if (!Array.isArray(savedArray) || !savedArray.length) {
    return { annotations: {} };
  }

  const annotations = {};
  savedArray.forEach((saved, idx) => {
    const resolved = _resolveOne(saved);
    if (!resolved) return;
    const key = saved.id || `annotation_${idx}`;
    annotations[key] = resolved;
  });

  return { annotations };
}
